/**
 * menuTree.ts — Sumber tunggal (single source of truth) untuk data menu sidebar.
 *
 * Adopsi Fase 2 pola sidebar MGMX (lihat cowork/docs/ADOPSI-MGMX-SIDEBAR-PLAN.md).
 * File ini dipindah dari `src/components/Sidebar.tsx` supaya:
 *  1. Sidebar.tsx fokus pada rendering/UX, bukan menampung 400+ baris data.
 *  2. Helper lain (mis. `buildBreadcrumbForMenu()` di Fase 3) dapat import
 *     data yang sama tanpa membuat siklus impor ke komponen React.
 *  3. Derived maps (`MENU_URL_MAP`, `MENU_KDMENU_MAP`, `GROUP_ICON`, `SUBGROUP_ICON`)
 *     tersedia untuk konsumen lain (mis. welcome dashboard, breadcrumb, quick-nav).
 *
 * Perilaku Sidebar tetap identik — cuma reorganisasi kode.
 */

import type { ElementType } from "react";
import {
    BarChart2,
    Bell,
    BookOpen,
    Building2,
    Database,
    Disc,
    DollarSign,
    FileText,
    Package,
    RefreshCw,
    Settings,
    ShieldCheck,
    Truck,
    User,
    Wallet,
} from "lucide-react";

// ─── Types ────────────────────────────────────────────────────────────────────

export interface SubMenuItem {
    name: string;
    href?: string;
    icon: ElementType;
    items?: SubMenuItem[];
    /** KdMMenu code — undefined = no restriction (always show). Array = OR-semantics. */
    kdMenu?: string | string[];
}

export interface MenuGroup {
    name: string;
    icon: ElementType;
    items: SubMenuItem[];
    isNested?: boolean;
}

// ─── Color accent per module group ────────────────────────────────────────────

export const GROUP_COLORS: Record<string, { dot: string; activeBg: string; activeText: string }> = {
    "Trucking": { dot: "bg-sky-400", activeBg: "bg-sky-500/15", activeText: "text-sky-200" },
    "Inventory": { dot: "bg-green-400", activeBg: "bg-green-500/15", activeText: "text-green-200" },
    "Pembelian": { dot: "bg-orange-400", activeBg: "bg-orange-500/15", activeText: "text-orange-200" },
    "Hutang": { dot: "bg-red-400", activeBg: "bg-red-500/15", activeText: "text-red-200" },
    "Penjualan": { dot: "bg-emerald-400", activeBg: "bg-emerald-500/15", activeText: "text-emerald-200" },
    "Piutang": { dot: "bg-teal-400", activeBg: "bg-teal-500/15", activeText: "text-teal-200" },
    "Kas Bank": { dot: "bg-violet-400", activeBg: "bg-violet-500/15", activeText: "text-violet-200" },
    "General Ledger": { dot: "bg-indigo-400", activeBg: "bg-indigo-500/15", activeText: "text-indigo-200" },
    "System": { dot: "bg-slate-400", activeBg: "bg-slate-500/15", activeText: "text-slate-200" },
};

// ─── Group / SubGroup icon maps (adopsi pola MGMX) ────────────────────────────

/** Icon per group utama — MGMX pattern (GROUP_ICON). */
export const GROUP_ICON: Record<string, ElementType> = {
    "Trucking": Truck,
    "Inventory": Package,
    "Pembelian": DollarSign,
    "Hutang": Wallet,
    "Penjualan": FileText,
    "Piutang": DollarSign,
    "Kas Bank": Wallet,
    "General Ledger": BookOpen,
    "System": Settings,
};

/** Icon per subgroup — MGMX pattern (SUBGROUP_ICON). */
export const SUBGROUP_ICON: Record<string, ElementType> = {
    "Master": Database,
    "Transaksi": FileText,
    "Laporan": BarChart2,
    "Setting": Settings,
};

// ─── Menu data — struktur & penamaan sesuai Delphi MainForm1.dfm ──────────────

export const menuGroups: MenuGroup[] = [
    {
        name: "Trucking",
        icon: Truck,
        items: [
            {
                name: "Master",
                icon: Database,
                items: [
                    { name: "Master Sektor", href: "/master/sektor", icon: Truck, kdMenu: "MSektor" },
                    { name: "Master Supir", href: "/master/supir", icon: User, kdMenu: "MSupir" },
                    { name: "Master Kernet", href: "/master/kernet", icon: User, kdMenu: "MKernet" },
                    { name: "Master Agen", href: "/master/agen", icon: User, kdMenu: "MSupir" },
                    { name: "Master Refunder", href: "/master/refunder", icon: User, kdMenu: "MRefunder" },
                    { name: "Master Truck", href: "/master/truck", icon: Truck, kdMenu: "MTruck" },
                    { name: "Master Jenis Truck", href: "/master/jenis-truck", icon: Truck, kdMenu: "MJenisTruck" },
                    { name: "Master Biaya", href: "/master/biaya", icon: Wallet, kdMenu: "MBrg" },
                    { name: "Harga Borongan Agen", href: "/master/hb-agen", icon: DollarSign, kdMenu: "MHBAgen" },
                    { name: "Master Tanggal", href: "/master/tanggal", icon: Database, kdMenu: "MTanggalCustom" },
                    { name: "Master Uang Jalan", href: "/master/uang-jalan", icon: Wallet, kdMenu: "MGTRMHBMassal" },
                    { name: "Harga Jual (Unit)", href: "/master/hj-unit", icon: DollarSign, kdMenu: "MGTRMHJMassalUnit" },
                    { name: "Harga Jual (KG)", href: "/master/hj-kg", icon: DollarSign, kdMenu: "MGTRMHJMassalKG" },
                    { name: "Master Komisi Supir Per Sektor", href: "/master/komisi-supir-sektor", icon: DollarSign, kdMenu: "MHB8" },
                    { name: "Master Komisi Kernet Per Sektor", href: "/master/komisi-kernet-sektor", icon: DollarSign, kdMenu: "MHB8" },
                    // Delphi MainForm.pas:13830 → SetMMenu(163, 'MAutoItem', 'Master Jenis Barang', ...)
                    // gated by SettingMenuTrucking=1. Halaman ini memakai tabel MGSYAutoItem filter
                    // Item='TRUCKING_BRG' (data: SEMEN, PASIR, dst) — dipakai sbg klasifikasi
                    // muatan/cargo saat input Order Trucking (MGTRTOrderTruckingInputForm.pas:3190),
                    // TIDAK ADA hubungan dgn Master Barang (mginmbrg, di grup Inventory di bawah).
                    // Dipindah dari grup Inventory ke sini 2026-09-22 (aga) + label ditambah
                    // "Trucking" biar tidak rancu dgn field "Kategori Barang" di Master Barang.
                    { name: "Master Jenis Barang Trucking", href: "/master/inventory-jenis-barang", icon: Package, kdMenu: "MAutoItem" },
                ]
            },
            {
                name: "Transaksi",
                icon: FileText,
                items: [
                    { name: "Order Trucking", href: "/transaksi/order-trucking", icon: FileText, kdMenu: "TOrderTrucking" },
                    { name: "Surat Jalan Customer", href: "/transaksi/surat-jalan", icon: FileText, kdMenu: "TSahSJ" },
                    { name: "Surat Jalan Kembali", href: "/transaksi/surat-jalan-kembali", icon: FileText, kdMenu: "TSahSJ" },
                    { name: "Surat Angkutan (Memo)", href: "/transaksi/surat-angkutan", icon: FileText, kdMenu: "TSJ" },
                    { name: "Surat Angkutan Kembali", href: "/transaksi/surat-angkutan-kembali", icon: FileText, kdMenu: "TSahSJ" },
                    { name: "Invoice Penjualan", href: "/transaksi/invoice", icon: FileText, kdMenu: "TJual" },
                    // "Penjualan Ban" & "Pemakaian Sparepart" dipindah ke grup Pembelian > Transaksi
                    // (2026-10-19) — mengikuti menu desktop (MainForm.dfm: menu "Pembelian & Pemakaian Sparepart").
                ]
            },
            {
                name: "Laporan",
                icon: BarChart2,
                items: [
                    { name: "Laporan Kegiatan Trucking Harian", href: "/laporan/kegiatan-trucking-harian", icon: FileText, kdMenu: "LStatusTruckingHarian" },
                    { name: "Laporan Surat Jalan Harian", href: "/laporan/harian-surat-jalan", icon: FileText, kdMenu: "LSuratJalanHrn" },
                    { name: "Posisi Uang Muka Supir", href: "/laporan/posisi-uang-muka-supir", icon: FileText, kdMenu: "LPosUMSupir" },
                    { name: "Kartu Uang Muka Supir", href: "/laporan/kartu-uang-muka-supir", icon: FileText, kdMenu: "LKUMSupir" },
                    { name: "Posisi Truck", href: "/laporan/posisi-truck", icon: FileText, kdMenu: "LPosTruck" },
                    { name: "Analisa Jarak Per Sektor Per Truck", href: "/laporan/analisa-jarak-sektor-truck", icon: FileText, kdMenu: "LAnanlisaJrkSektorTrk" },
                    { name: "Perubahan HJ Borongan SJ per Sektor", href: "/laporan/perubahan-hj-borongan-sektor", icon: FileText, kdMenu: "LPerubahanHJBoroganSJPerSektor" },
                    { name: "Omset Harian per Nopol", href: "/laporan/omset-harian-per-nopol", icon: FileText, kdMenu: "LOmsetHrnPerNopol" },
                    { name: "History Truck (KM)", href: "/laporan/history-truck-km", icon: FileText, kdMenu: "LHistoryTruckBerdasarkanKMTruck" },
                    { name: "Komisi Supir dan Kernet", href: "/laporan/komisi-supir-kernet", icon: FileText, kdMenu: "LKomisiSupirKernet" },
                    { name: "Tanggal Berlaku STNK", href: "/laporan/tgl-berlaku-stnk", icon: FileText, kdMenu: "LTglBerlakuSTNK" },
                ]
            }
        ]
    },
    {
        name: "Inventory",
        icon: Package,
        items: [
            {
                name: "Master",
                icon: Database,
                items: [
                    { name: "Master Satuan", href: "/master/satuan", icon: Package, kdMenu: "MStn" },
                    // CATATAN: entry lama "Master Jenis Barang" (/master/jenis-barang) dan
                    // "Master Harga Jenis Barang" (/master/harga-jenis-barang) dihapus 2026-09-03 —
                    // keduanya duplikat byte-identik yang memanggil MGTRMHJenisBarangRoutes (tabel
                    // riwayat harga), tapi di Delphi asli MGTRMHJenisBarang BUKAN menu berdiri sendiri,
                    // melainkan sub-panel di dalam form MGSYAutoItemInputForm.dfm.
                    //
                    // "Master Jenis Barang" (MAutoItem/MGSYAutoItem) dipindah ke grup Trucking > Master
                    // 2026-09-22 (aga) — lihat komentar di sana.
                    //
                    // "Master Jenis Barang" (MJenisBrg/mginmjenisbrg) DIBANGUN 2026-09-26 (Batch 5
                    // lanjutan, sesi adopsi MGMX) — override Decision 2026-09-22. Endpoint dedicated
                    // baru `/api/master/jenis-barang`. Field kondisional Delphi (IdxPlastik,
                    // HargaJualDi*, OngkosKirim) di-SKIP karena KONSTAN MGTRUCK setting-nya 0.
                    { name: "Master Jenis Barang", href: "/master/jenis-barang", icon: Package, kdMenu: "MJenisBrg" },
                    // Master Golongan (MGol/mginmgol) DIBANGUN 2026-09-26 (Batch 5 lanjutan,
                    // sesi adopsi MGMX). Delphi ref: MGINMGolForm.pas. TipeData enum 0/1/2/3.
                    // Field MGMX Web `TampilStock` SKIP (tidak ada di DDL MGTRUCK).
                    { name: "Master Golongan", href: "/master/golongan", icon: Package, kdMenu: "MGol" },
                    { name: "Master Barang", href: "/master/barang", icon: Package, kdMenu: "MBrg" },
                    { name: "Master Gudang", href: "/master/gudang", icon: Package, kdMenu: "MGudang" },
                    { name: "Saldo Awal Barang", href: "/master/saldo-awal-barang", icon: Database, kdMenu: "TSABrg" },
                    { name: "Setting Minimal Stock", href: "/master/inventory/min-stock", icon: Package, kdMenu: "TMinStock" },
                    // Delphi MainForm.pas:13752 → SetMMenu(152, 'StockSN', 'Stock Serial Number', ...).
                    // Label dikembalikan sesuai Delphi asli — label lama "Input Serial Number Stock"
                    // adalah nama karangan yang tidak match Delphi.
                    { name: "Stock Serial Number", href: "/master/inventory/stock-serial-number", icon: Database, kdMenu: "StockSN" },
                ]
            },
            {
                name: "Transaksi",
                icon: FileText,
                items: [
                    { name: "Konversi Barang", href: "/transaksi/konversi-barang", icon: Package, kdMenu: "TKonvBrg" },
                    { name: "Print Form Opname Stock", href: "/transaksi/opname-stock", icon: Package, kdMenu: "TPrintOpname" },
                    { name: "Input Hasil Opname Stock", href: "/transaksi/input-hasil-opname", icon: Package, kdMenu: "TInputOpname" },
                    { name: "Penyesuaian Stock", href: "/transaksi/penyesuaian-stock", icon: Package, kdMenu: "TPenyesuaian" },
                    { name: "Transfer Barang (Kirim)", href: "/transaksi/transfer-barang-kirim", icon: Package, kdMenu: "TTransferKirim" },
                    { name: "Penggantian Ban", href: "/transaksi/ganti-ban", icon: RefreshCw, kdMenu: "TGantiBan" },
                ]
            },
            {
                name: "Laporan",
                icon: BarChart2,
                items: [
                    { name: "Daftar Barang", href: "/laporan/daftar-barang", icon: Package, kdMenu: "LMBrgList" },
                    { name: "Informasi Barang", href: "/laporan/informasi-barang", icon: Package, kdMenu: "LMBrgInfo" },
                    { name: "Stock Barang", href: "/master/inventory/stock-barang", icon: Package, kdMenu: "StockBrg" },
                    { name: "Posisi Stock Barang", href: "/laporan/posisi-stock", icon: Package, kdMenu: "LPosStock" },
                    { name: "Stock Barang Nilai", href: "/laporan/stock-barang", icon: Package, kdMenu: "LStockNilai" },
                    { name: "Stock per Supplier", href: "/laporan/stock-per-supplier", icon: Package, kdMenu: "LStockSup" },
                    { name: "Laporan Minimal Stock", href: "/laporan/minimal-stock", icon: Package, kdMenu: "LMinStock" },
                    { name: "Analisa Pemesanan", href: "/laporan/analisa-pemesanan", icon: Package, kdMenu: "LAnalisaOrder" },
                    { name: "Kartu Stock Barang", href: "/laporan/kartu-stock", icon: Package, kdMenu: "LKStock" },
                    { name: "Kartu Stock per Gudang", href: "/laporan/kartu-stock-gudang", icon: Package, kdMenu: "LKStockGd" },
                    { name: "Kartu Stock Available", href: "/inventory/kartu-stock-available", icon: Package, kdMenu: "LKStockWoHPP" },
                    { name: "Kartu Stock Serial Number", href: "/laporan/kartu-stock-sn", icon: Database, kdMenu: "LKartuStockSN" },
                    { name: "Kartu Stock SN (Full HPP)", href: "/laporan/kartu-stock-sn-full-hpp", icon: Database, kdMenu: "LKartuStockSNwHPP" },
                    { name: "Laporan Hasil Opname Stock", href: "/laporan/hasil-opname", icon: Package, kdMenu: "LHasilOpname" },
                    { name: "LPB Transfer Barang", href: "/laporan/lpb-transfer-barang", icon: Package, kdMenu: "LLPBTransfer" },
                    { name: "History Ban Per SN", href: "/laporan/history-ban-per-sn", icon: FileText, kdMenu: "LHistoryBanPerSN" },
                    { name: "Posisi Stock Serial Number", href: "/laporan/posisi-stock-sn", icon: Database, kdMenu: "LPosisiStockSN" },
                    { name: "Laporan Harga Beli Barang", href: "/laporan/harga-beli-barang", icon: FileText, kdMenu: "LBrgHPP" },
                    { name: "Laporan Rekapitulasi Stock", href: "/laporan/rekap-stock", icon: Package, kdMenu: "LRekapStock" },
                    { name: "Laporan Peminjaman Barang Harian", href: "/inventory/laporan-peminjaman-barang-harian", icon: Package, kdMenu: "LTPinjamBrg" },
                    { name: "Laporan Pengembalian Barang Harian", href: "/inventory/laporan-pengembalian-barang-harian", icon: Package, kdMenu: "LTKembaliBrg" },
                ]
            }
        ]
    },
    {
        name: "Pembelian",
        icon: DollarSign,
        items: [
            {
                name: "Master",
                icon: Database,
                items: [
                    { name: "Master Supplier", href: "/master/pembelian/supplier", icon: User, kdMenu: "MSup" },
                    // Master Jenis Supplier (MJenisSup) DIBANGUN 2026-09-26 (Batch 5 lanjutan,
                    // sesi adopsi MGMX). Delphi ref: MGAPMJenisSupForm.pas. ID<50 reserved sistem
                    // (Supir/Kernet/Refunder) — reserved rows tetap tampil di list tapi Edit/Delete
                    // di-disable (dikelola modul lain). Field IsKomisi+Kategori SKIP (CSettingKomisiSales=0).
                    { name: "Master Jenis Supplier", href: "/master/jenis-supplier", icon: User, kdMenu: "MJenisSup" },
                ]
            },
            {
                name: "Transaksi",
                icon: FileText,
                items: [
                    { name: "Purchase Order", href: "/transaksi/po-pembelian", icon: FileText, kdMenu: "TPOBeli" },
                    { name: "Pembelian", href: "/transaksi/pembelian", icon: FileText, kdMenu: "TBeli" },
                    { name: "Retur Pembelian", href: "/transaksi/retur-pembelian", icon: FileText, kdMenu: "TRBeli" },
                    { name: "Uang Muka Pembelian", href: "/transaksi/uang-muka-pembelian", icon: FileText, kdMenu: "TUMBeli" },
                    // Delphi MainForm.dfm: menu Pembelian memuat Pemakaian Sparepart (ActionTPemakaianSparepart)
                    // dan Penjualan Ban (ActionTJualBan) — bukan di menu Trucking.
                    { name: "Pemakaian Sparepart", href: "/transaksi/pemakaian-sparepart", icon: FileText, kdMenu: "TPemakaianSparepart" },
                    { name: "Penjualan Ban", href: "/transaksi/penjualan-ban", icon: Disc, kdMenu: "TJualBan" },
                ]
            },
            {
                name: "Laporan",
                icon: BarChart2,
                items: [
                    // Selaras MGMX Web (mgsymmenu.IsShow) — hanya laporan aktif di MGMX yang di-expose.
                    // Non-aktif di MGMX (di-hide dari sidebar, route/page tetap ada untuk future re-enable):
                    //   - LMSupList "Daftar Supplier" (341)
                    //   - LTBeliHrn "Laporan Pembelian Harian" (342)
                    //   - LTRBeliHrn "Laporan Retur Pembelian Harian" (343)
                    //   - LTPOHrn "Laporan PO Harian" (347)
                    { name: "Perolehan PPN dan PPH", href: "/laporan/perolehan-ppn-pph", icon: FileText, kdMenu: "LPPHPPN" }, // MGTRUCK-specific, tidak ada di MGMX
                    { name: "Laporan Realisasi PO", href: "/laporan/realisasi-po", icon: FileText, kdMenu: "LRealPO" },
                    { name: "Laporan Outstanding PO", href: "/laporan/outstanding-po", icon: FileText, kdMenu: "LOSPO" },
                    { name: "FP Pembelian Harian", href: "/laporan/fp-pembelian-harian", icon: FileText, kdMenu: "LFPBeliHrn" },
                    { name: "Rekap FP Pembelian", href: "/laporan/rekap-fp-pembelian", icon: FileText, kdMenu: "LRekapFPBeli" },
                    { name: "Pajak UM Pembelian Harian", href: "/laporan/pajak-um-pembelian-harian", icon: FileText, kdMenu: "LUMFPBeliHrn" },
                ]
            }
        ]
    },
    {
        name: "Hutang",
        icon: Wallet,
        items: [
            {
                name: "Master",
                icon: Database,
                items: [
                    { name: "Saldo Awal Hutang", href: "/master/hutang/saldo-awal-hutang", icon: Wallet, kdMenu: "TSAHut" },
                ]
            },
            {
                name: "Transaksi",
                icon: FileText,
                items: [
                    { name: "Uang Muka Supir", href: "/uang-muka-supir", icon: DollarSign, kdMenu: "TUMBeliSupir" },
                    { name: "Pembayaran Hutang", href: "/pembayaran-hutang-supplier", icon: DollarSign, kdMenu: "TBHut" },
                    { name: "Pembayaran Hutang Supir", href: "/pembayaran-hutang-supir", icon: DollarSign, kdMenu: "TBHutSupir" },
                    { name: "Koreksi Hutang CN", href: "/koreksi-hutang-cn", icon: DollarSign, kdMenu: "TKorHutCN" },
                    { name: "Koreksi Hutang DN", href: "/koreksi-hutang-dn", icon: DollarSign, kdMenu: "TKorHutDN" },
                    { name: "Koreksi Hutang Supir", href: "/koreksi-hutang-supir", icon: DollarSign, kdMenu: "TKorHutSupir" },
                    { name: "Koreksi Hutang Refunder", href: "/koreksi-hutang-refunder", icon: DollarSign, kdMenu: "TKorHutRefunder" },
                    { name: "Pembayaran Hutang Refunder", href: "/pembayaran-hutang-refunder", icon: DollarSign, kdMenu: "TBHutRefunder" },
                    { name: "Uang Muka Pembelian Refunder", href: "/transaksi/uang-muka-pembelian-refunder", icon: DollarSign, kdMenu: "TUMBeliRefunder" },
                ]
            },
            {
                name: "Laporan",
                icon: BarChart2,
                items: [
                    { name: "Laporan Posisi Hutang", href: "/laporan/posisi-hutang", icon: FileText, kdMenu: "LPosHut" },
                    { name: "Kartu Hutang", href: "/laporan/kartu-hutang", icon: FileText, kdMenu: "LKartuHut" },
                    { name: "Kartu Hutang per Transaksi", href: "/laporan/kartu-hutang-per-transaksi", icon: FileText, kdMenu: "LKHutTrans" },
                    { name: "Kartu Hutang & Piutang Supir", href: "/laporan/kartu-hutang-piutang-supir", icon: FileText, kdMenu: "LKartuHutPiutSupir" },
                    { name: "Laporan Hutang Jatuh Tempo", href: "/laporan/hutang-jatuh-tempo", icon: FileText, kdMenu: "LHutJatuhTempo" },
                    { name: "Laporan Pembayaran Hutang Harian", href: "/laporan/pembayaran-hutang-harian", icon: FileText, kdMenu: "LTBHutHrn" },
                    { name: "Laporan Koreksi Hutang Harian", href: "/laporan/koreksi-hutang-harian", icon: FileText, kdMenu: "LTKorHutHrn" },
                    { name: "Laporan Posisi UM Pembelian", href: "/laporan/posisi-um-pembelian", icon: FileText, kdMenu: "LPosUMBeli" },
                    { name: "Laporan Kartu UM Pembelian", href: "/laporan/kartu-um-pembelian", icon: FileText, kdMenu: "LKUMBeli" },
                    { name: "Laporan Rekap Mutasi Hutang", href: "/laporan/rekap-mutasi-hutang", icon: FileText, kdMenu: "LSumRekapHut" },
                    { name: "Laporan Harian DM", href: "/laporan/harian-dm", icon: FileText, kdMenu: "LHrnWBT" },
                    { name: "Laporan Realisasi DM", href: "/laporan/realisasi-dm", icon: FileText, kdMenu: "LRealisasiWBT" },
                    { name: "Laporan Outstanding DM", href: "/laporan/outstanding-dm", icon: FileText, kdMenu: "LOutstandingWBT" },
                ]
            }
        ]
    },
    {
        name: "Penjualan",
        icon: FileText,
        items: [
            {
                name: "Master",
                icon: Database,
                items: [
                    { name: "Master Customer", href: "/master/customer", icon: User, kdMenu: "MCust" },
                    { name: "Master Kota", href: "/master/kota", icon: Database, kdMenu: "MKota" },
                    { name: "Master Jenis Customer", href: "/master/jenis-customer", icon: User, kdMenu: "MJenisCustomer" },
                    { name: "Master Setting Limit Piutang", href: "/master/limit-piutang-customer", icon: DollarSign, kdMenu: "MCustLimitPiut" },
                ]
            },
            {
                name: "Transaksi",
                icon: FileText,
                items: [
                    { name: "Saldo Awal UM Penjualan", href: "/transaksi/saldo-awal-um-penjualan", icon: DollarSign, kdMenu: "TSAUMJual" },
                ]
            },
            {
                name: "Laporan",
                icon: BarChart2,
                items: [
                    { name: "Daftar Harga Jual", href: "/laporan/daftar-harga-jual", icon: FileText, kdMenu: "LMHJList" },
                    { name: "Laporan Pakai DP Harian", href: "/laporan/pakai-dp-customer", icon: FileText, kdMenu: "LPakaiUangMukaHrn" },
                    { name: "Penjualan Cabang", href: "/laporan/penjualan-cabang", icon: Building2, kdMenu: "LPenjualanCabang" },
                    { name: "Penjualan Ban", href: "/laporan/penjualan-ban", icon: Disc, kdMenu: "LPenjualanBan" },
                    { name: "FP Penjualan Harian", href: "/laporan/fp-penjualan-harian", icon: FileText, kdMenu: "LFPJualHrn" },
                    { name: "Rekap FP Penjualan", href: "/laporan/rekap-fp-penjualan", icon: FileText, kdMenu: "LRekapFPJual" },
                    { name: "Pajak UM Penjualan Harian", href: "/laporan/pajak-um-penjualan-harian", icon: FileText, kdMenu: "LUMFPJualHrn" },
                    { name: "Omzet Customer", href: "/laporan/omzet-customer", icon: FileText, kdMenu: "LOmzetCust" },
                    { name: "Daftar Customer", href: "/laporan/daftar-customer-laporan", icon: User, kdMenu: "LDftCust" },
                    { name: "Grafik Penjualan", href: "/laporan/grafik-penjualan", icon: BarChart2, kdMenu: "LGrafikJual" },
                    { name: "Rugi Laba Penjualan", href: "/laporan/rugi-laba-penjualan", icon: FileText, kdMenu: "LRLKotor" },
                    { name: "Rugi Laba per Barang", href: "/laporan/rugi-laba-per-barang", icon: Package, kdMenu: "LRLPerBarang" },
                ]
            }
        ]
    },
    {
        name: "Piutang",
        icon: DollarSign,
        items: [
            {
                name: "Transaksi",
                icon: FileText,
                items: [
                    { name: "Saldo Awal Piutang", href: "/transaksi/saldo-awal-piutang", icon: DollarSign, kdMenu: "TSAPiut" },
                    { name: "Titip Uang Muka Penjualan", href: "/transaksi/uang-muka-penjualan", icon: DollarSign, kdMenu: "TUMJual" },
                    { name: "Pembayaran Piutang", href: "/transaksi/pembayaran-piutang", icon: DollarSign, kdMenu: "TBPiut" },
                    { name: "Koreksi Piutang CN", href: "/transaksi/koreksi-piutang-cn", icon: DollarSign, kdMenu: "TKorPiutCN" },
                    { name: "Koreksi Piutang DN", href: "/transaksi/koreksi-piutang-dn", icon: DollarSign, kdMenu: "TKorPiutDN" },
                ]
            },
            {
                name: "Laporan",
                icon: BarChart2,
                items: [
                    { name: "Laporan Posisi Piutang", href: "/laporan/posisi-piutang", icon: FileText, kdMenu: "LPosPiut" },
                    { name: "Laporan Posisi Uang Muka Penjualan", href: "/laporan/posisi-um-penjualan", icon: FileText, kdMenu: "LPosUMJual" },
                    { name: "Kartu Piutang", href: "/laporan/kartu-piutang", icon: FileText, kdMenu: "LKartuPiut" },
                    { name: "Kartu Piutang per Transaksi", href: "/laporan/kartu-piutang-per-transaksi", icon: FileText, kdMenu: "LKPiutTrans" },
                    { name: "Laporan Kartu Uang Muka Penjualan", href: "/laporan/kartu-um-penjualan", icon: FileText, kdMenu: "LKUMJual" },
                    { name: "Laporan Titip Uang Muka Penjualan", href: "/laporan/titip-um-penjualan", icon: FileText, kdMenu: "LTitipUMJual" },
                    { name: "Umur Piutang", href: "/laporan/umur-piutang", icon: FileText, kdMenu: "LUmurPiut" },
                    { name: "Laporan Piutang Jatuh Tempo", href: "/laporan/piutang-jatuh-tempo", icon: FileText, kdMenu: "LPiutJatuhTempo" },
                    { name: "Laporan Piutang Belum Terbayar", href: "/laporan/piutang-belum-terbayar", icon: FileText, kdMenu: "LSisaPiut" },
                    { name: "Invoice Harian", href: "/laporan/invoice-harian", icon: FileText, kdMenu: "LInvoiceHarian" },
                    { name: "Pemakaian UM Penjualan Harian", href: "/laporan/pemakaian-um-penjualan-harian", icon: FileText, kdMenu: "LPakaiUangMukaHrn" },
                    { name: "Pemakaian Sparepart Harian", href: "/laporan/pemakaian-sparepart-harian", icon: FileText, kdMenu: "LTPemakaianSparepart" },
                    { name: "Rekap Pembayaran Piutang", href: "/laporan/rekap-pembayaran-piutang", icon: FileText, kdMenu: "LRekapBayarPiut" },
                    { name: "Laporan Pembayaran Piutang Harian", href: "/laporan/pembayaran-piutang-harian", icon: FileText, kdMenu: "LTBPiutHrn" },
                    { name: "Laporan Koreksi Piutang Harian", href: "/laporan/koreksi-piutang-harian", icon: FileText, kdMenu: "LKorPiutHrn" },
                    { name: "Summary Rekap Mutasi Piutang", href: "/laporan/summary-rekap-mutasi-piutang", icon: FileText, kdMenu: "LSummaryRekapMutasiPiut" },
                    { name: "Summary Posisi Piutang", href: "/laporan/summary-posisi-piutang", icon: FileText, kdMenu: "LSumPosPiut" },
                ]
            }
        ]
    },
    {
        name: "Kas Bank",
        icon: Wallet,
        items: [
            {
                name: "Master",
                icon: Database,
                items: [
                    { name: "Master Kas", href: "/master/kas", icon: Wallet, kdMenu: "MKas" },
                    { name: "Master Bank", href: "/master/bank", icon: Building2, kdMenu: "MBank" },
                    { name: "Master Rekening", href: "/master/rekening", icon: Wallet, kdMenu: "MRek" },
                    { name: "Master Giro Masuk", href: "/master/giro-masuk", icon: Wallet, kdMenu: "MGiroM" },
                    { name: "Master Giro Keluar", href: "/master/giro-keluar", icon: Wallet, kdMenu: "MGiroK" },
                    { name: "Saldo Awal Kas", href: "/master/saldo-awal-kas", icon: Wallet, kdMenu: "TSAKas" },
                    { name: "Saldo Awal Rekening", href: "/master/saldo-awal-rekening", icon: Wallet, kdMenu: "TSARek" },
                ]
            },
            {
                name: "Transaksi",
                icon: FileText,
                items: [
                    { name: "Mutasi Kas & Bank", href: "/transaksi/mutasi-kas-bank", icon: Wallet, kdMenu: ["TKasK", "TKasM"] },
                    { name: "Pencairan Giro Masuk", href: "/transaksi/giro-cair-masuk", icon: Wallet, kdMenu: "TGiroCairM" },
                    { name: "Pencairan Giro Keluar", href: "/transaksi/giro-cair-keluar", icon: Wallet, kdMenu: "TGiroCairK" },
                    { name: "Tolakan Giro Masuk", href: "/transaksi/giro-tolak-masuk", icon: Wallet, kdMenu: "TGiroTolakM" },
                    { name: "Tolakan Giro Keluar", href: "/transaksi/giro-tolak-keluar", icon: Wallet, kdMenu: "TGiroTolakK" },
                    { name: "Penggantian Giro Masuk", href: "/transaksi/giro-ganti-masuk", icon: Wallet, kdMenu: "TGiroGantiM" },
                    { name: "Penggantian Giro Keluar", href: "/transaksi/giro-ganti-keluar", icon: Wallet, kdMenu: "TGiroGantiK" },
                ]
            },
            {
                name: "Laporan",
                icon: BarChart2,
                items: [
                    { name: "Laporan Posisi Kas", href: "/laporan/posisi-kas", icon: FileText, kdMenu: "LPosKas" },
                    { name: "Laporan Posisi Bank", href: "/laporan/posisi-bank", icon: FileText, kdMenu: "LPosBank" },
                    { name: "Kartu Kas", href: "/laporan/kartu-kas", icon: FileText, kdMenu: "LKKas" },
                    { name: "Kartu Bank", href: "/laporan/kartu-bank", icon: FileText, kdMenu: "LKBank" },
                    { name: "Laporan Transaksi Pencairan Giro Harian", href: "/laporan/giro-cair-harian", icon: FileText, kdMenu: "LGiroCairHrn" },
                    { name: "Status Giro", href: "/laporan/status-giro", icon: FileText, kdMenu: "LStatusGiro" },
                    { name: "Kartu Giro", href: "/laporan/kartu-giro", icon: FileText, kdMenu: "LKGiro" },
                    { name: "Laporan Mutasi Kas Harian", href: "/laporan/mutasi-kas-harian", icon: FileText, kdMenu: "LTTransferHrn" },
                ]
            }
        ]
    },
    {
        name: "General Ledger",
        icon: BookOpen,
        items: [
            {
                name: "Master",
                icon: Database,
                items: [
                    { name: "Master Jenis Jurnal", href: "/general-ledger/jenis-jurnal", icon: FileText, kdMenu: "MJenisJurnal" },
                    { name: "Master Perkiraan", href: "/general-ledger/master-perkiraan", icon: BookOpen, kdMenu: "MPerkiraan" },
                    { name: "Saldo Awal Perkiraan", href: "/general-ledger/saldo-awal-perkiraan", icon: Wallet, kdMenu: "TSAPerkiraan" },
                    { name: "Setting Perkiraan", href: "/system/setting-perkiraan", icon: BookOpen, kdMenu: "SettingPerkiraan" },
                ]
            },
            {
                name: "Transaksi",
                icon: FileText,
                items: [
                    { name: "Jurnal Memo", href: "/general-ledger/jurnal-memo", icon: FileText, kdMenu: "TJurnalMemo" },
                    { name: "Posting", href: "/general-ledger/posting", icon: FileText, kdMenu: "MPost" },
                    { name: "Posting Harian", href: "/general-ledger/posting-harian", icon: FileText, kdMenu: "MPostHarian" },
                    { name: "Batal Posting", href: "/general-ledger/batal-posting", icon: FileText, kdMenu: "MBatalPost" },
                ]
            },
            {
                name: "Laporan",
                icon: BarChart2,
                items: [
                    { name: "Laporan Jurnal Harian", href: "/laporan/jurnal-harian", icon: FileText, kdMenu: "LJurnalHrn" },
                    { name: "Buku Besar", href: "/laporan/buku-besar", icon: BookOpen, kdMenu: "LBukuBsr" },
                    { name: "Laporan Rugi Laba", href: "/laporan/rugi-laba", icon: FileText, kdMenu: "LRugiLaba" },
                    { name: "Neraca", href: "/laporan/neraca", icon: FileText, kdMenu: "LNeraca" },
                    { name: "Laporan Progress Rugi Laba", href: "/laporan/progress-rugi-laba", icon: FileText, kdMenu: "LProgressRugiLaba" },
                ]
            }
        ]
    },
    {
        name: "System",
        icon: Settings,
        items: [
            {
                name: "Master",
                icon: Database,
                items: [
                    { name: "Global Information", href: "/system/global-info", icon: Settings, kdMenu: "GlobalInfo" },
                    { name: "Level Manager", href: "/system/level-manager", icon: ShieldCheck, kdMenu: "TLevelManager" },
                    { name: "Master User", href: "/master/user", icon: User, kdMenu: "MUser" },
                    { name: "Setting Reminder", href: "/system/setting-reminder", icon: Bell, kdMenu: "SettingReminder" },
                    { name: "Setting Default Reminder", href: "/system/setting-default-reminder", icon: Bell, kdMenu: "SettingDefaultReminder" },
                ]
            },
            {
                name: "Transaksi",
                icon: FileText,
                items: [
                    { name: "Recalculate Data", href: "/system/recalculate", icon: RefreshCw, kdMenu: "RepairData" },
                    { name: "Generate Master Data", href: "/system/generate-master-data", icon: Database, kdMenu: "MasterData" },
                    { name: "Reminder", href: "/system/reminder", icon: Bell, kdMenu: "Reminder" },
                ]
            }
        ]
    },
];

/** Single menu items (di luar tree — mis. Dashboard). */
export const SINGLE_MENU_ITEMS: Array<{ name: string; href: string; icon: ElementType }> = [
    { name: "Dashboard", href: "/dashboard", icon: Truck /* placeholder — Sidebar override dgn Home */ },
];

// ─── Derived maps (MGMX pattern — flat access untuk konsumen non-render) ──────

/**
 * Walk semua leaf items dari `menuGroups` — leaf = item yg tak punya `items[]` children.
 */
function walkLeafItems(): Array<{
    groupName: string;
    subGroupName: string;
    item: SubMenuItem;
}> {
    const out: Array<{ groupName: string; subGroupName: string; item: SubMenuItem }> = [];
    for (const g of menuGroups) {
        for (const sg of g.items) {
            // Level-2 (subgroup) selalu punya items — items[] adalah leaf level (kecuali nested lebih dalam)
            if (!sg.items) continue;
            for (const leaf of sg.items) {
                // Nested check — kalau leaf punya items (arbitrary depth), skip di sini (rare case)
                if (leaf.items) {
                    for (const deeper of leaf.items) {
                        out.push({ groupName: g.name, subGroupName: sg.name, item: deeper });
                    }
                } else {
                    out.push({ groupName: g.name, subGroupName: sg.name, item: leaf });
                }
            }
        }
    }
    return out;
}

/** Map `name → href` untuk semua leaf item. Skip item tanpa href. */
export const MENU_URL_MAP: Record<string, string> = (() => {
    const m: Record<string, string> = {};
    for (const { item } of walkLeafItems()) {
        if (item.href) m[item.name] = item.href;
    }
    return m;
})();

/** Map `name → kdMenu` untuk semua leaf item. Skip item tanpa kdMenu. */
export const MENU_KDMENU_MAP: Record<string, string | string[]> = (() => {
    const m: Record<string, string | string[]> = {};
    for (const { item } of walkLeafItems()) {
        if (item.kdMenu) m[item.name] = item.kdMenu;
    }
    return m;
})();

// ─── Helpers ──────────────────────────────────────────────────────────────────

/** Cek apakah subtree memiliki item aktif (match pathname). */
export function hasActiveInTree(items: SubMenuItem[], pathname: string): boolean {
    return items.some(item => {
        if (item.href && (pathname === item.href || pathname.startsWith(item.href + "/"))) return true;
        if (item.items) return hasActiveInTree(item.items, pathname);
        return false;
    });
}

// ─── Fase 3: Breadcrumb builder (adopsi pola MGMX) ────────────────────────────

/** 1 segment breadcrumb — bisa dgn siblings sebagai opsi navigasi cepat. */
export interface BreadcrumbSegment {
    label: string;
    href?: string;
    /** Siblings se-level untuk dropdown pindah antar menu. */
    options?: Array<{ label: string; href: string }>;
}

/**
 * Build breadcrumb 3-segment (Group / SubGroup / Item) untuk halaman menu tertentu.
 * Dipakai halaman detail (via `<BreadcrumbNav>` komponen) — mirror pola MGMX
 * `buildBreadcrumbForMenu()` di `mgmx_fe/src/components/Sidebar.tsx`.
 *
 * @param menuName — nama item persis seperti di `menuGroups` (mis. "Master Barang").
 * @returns Array 3 segment atau `null` kalau menu tidak ditemukan di tree.
 *
 * Contoh:
 *   buildBreadcrumbForMenu("Purchase Order")
 *   → [
 *       { label: "Pembelian", options: [{label:"Trucking",href:...}, ...] },
 *       { label: "Transaksi", options: [{label:"Master",href:...}, ...] },
 *       { label: "Purchase Order", href: "/transaksi/po-pembelian",
 *         options: [{label:"Pembelian",href:...}, ...] }
 *     ]
 *
 * Note: pakai `menuName` (bukan kdMenu) karena label lebih stabil untuk breadcrumb —
 * kdMenu bisa sama untuk 2 halaman berbeda (mis. shared endpoint). Sebelum lookup,
 * caller boleh convert kdMenu → menuName via reverse map (belum di-export).
 */
export function buildBreadcrumbForMenu(menuName: string): BreadcrumbSegment[] | null {
    for (const g of menuGroups) {
        for (const sg of g.items) {
            if (!sg.items) continue;
            const found = sg.items.find(leaf => leaf.name === menuName);
            if (!found) continue;

            // Group options — 1 entri per group, href = leaf pertama yg punya URL
            const groupOptions = menuGroups
                .map(gg => {
                    const firstLeaf = gg.items.flatMap(s => s.items ?? []).find(it => !!it.href);
                    return firstLeaf?.href ? { label: gg.name, href: firstLeaf.href } : null;
                })
                .filter((x): x is { label: string; href: string } => !!x);

            // SubGroup options — subgroup lain dalam group yg sama
            const subGroupOptions = g.items
                .map(ssg => {
                    const firstLeaf = (ssg.items ?? []).find(it => !!it.href);
                    return firstLeaf?.href ? { label: ssg.name, href: firstLeaf.href } : null;
                })
                .filter((x): x is { label: string; href: string } => !!x);

            // Item options — item lain dalam subgroup yg sama
            const itemOptions = (sg.items ?? [])
                .filter(it => !!it.href)
                .map(it => ({ label: it.name, href: it.href! }));

            return [
                { label: g.name, options: groupOptions },
                { label: sg.name, options: subGroupOptions },
                { label: found.name, href: found.href, options: itemOptions },
            ];
        }
    }
    return null;
}

/**
 * Reverse lookup: kdMenu → nama menu pertama yg pakai kode itu.
 * Dipakai kalau caller cuma pegang kdMenu (mis. dari BE) tapi butuh breadcrumb.
 * Return null kalau kode tak ditemukan.
 */
export function findMenuNameByKdMenu(kdMenu: string): string | null {
    for (const [name, code] of Object.entries(MENU_KDMENU_MAP)) {
        if (Array.isArray(code)) {
            if (code.includes(kdMenu)) return name;
        } else if (code === kdMenu) {
            return name;
        }
    }
    return null;
}
